<template>
  <v-dialog
    v-model="show"
    :persistent="isPersistent"
    max-width="500px"
    :close-on-back="!isPersistent"
  >
    <v-card class="pa-4">
      <v-card-title class="d-flex align-center justify-center">
        <v-icon
          :color="isPersistent ? 'error' : 'primary'"
          size="40"
          class="mr-3"
        >
          {{ isPersistent ? 'mdi-alert-circle' : 'mdi-update' }}
        </v-icon>
        <span class="text-h6">
          {{ isPersistent ? 'Actualización Requerida' : 'Actualización Disponible' }}
        </span>
      </v-card-title>

      <v-card-text class="text-center">
        <div v-if="isPersistent" class="mb-4">
          <v-alert
            type="error"
            variant="tonal"
            class="mb-4"
          >
            <strong>Tu versión de la aplicación está desactualizada y debe ser actualizada para continuar.</strong>
          </v-alert>
        </div>

        <div class="mb-4">
          <p class="text-body-1 mb-2">
            <strong>Versión Actual:</strong> {{ versionInfo.current_version }}
          </p>
          <p class="text-body-1 mb-2">
            <strong>Última Versión:</strong> {{ versionInfo.latest_version }}
          </p>
          <p v-if="versionInfo.minimum_version" class="text-body-1 mb-2">
            <strong>Versión Mínima:</strong> {{ versionInfo.minimum_version }}
          </p>
        </div>

        <div v-if="versionInfo.release_notes" class="mb-4">
          <v-expansion-panels variant="accordion">
            <v-expansion-panel>
              <v-expansion-panel-title>
                <v-icon class="mr-2">mdi-text-box-outline</v-icon>
                Notas de la Versión
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div v-html="versionInfo.release_notes"></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="text-body-2 text-medium-emphasis">
          {{ isPersistent
            ? 'Por favor, actualiza la página para obtener la última versión.'
            : 'Recomendamos actualizar para obtener las últimas mejoras y correcciones.'
          }}
        </div>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn
          @click="handleUpdate"
          :color="isPersistent ? 'error' : 'primary'"
          variant="elevated"
          size="large"
          class="px-8"
        >
          <v-icon class="mr-2">mdi-refresh</v-icon>
          {{ isPersistent ? 'Actualizar Ahora' : 'Actualizar' }}
        </v-btn>
        
        <v-btn
          v-if="!isPersistent"
          @click="handleLater"
          variant="text"
          size="large"
          class="px-8 ml-2"
        >
          Más Tarde
        </v-btn>
      </v-card-actions>

      <v-card-text class="text-center pt-0">
        <div class="text-caption text-medium-emphasis">
          <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
          La actualización se realizará automáticamente al recargar la página
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import * as Sentry from '@sentry/vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  versionInfo: {
    type: Object,
    required: true,
    default: () => ({
      current_version: '0.0.0',
      latest_version: '0.0.0',
      minimum_version: null,
      force_update: false,
      release_notes: null
    })
  },
  persistent: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'update', 'dismiss']);

const show = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const forceUpdate = computed(() => props.versionInfo.force_update);
const isPersistent = computed(() => props.persistent || forceUpdate.value);

const handleUpdate = () => {
  // Track user action en Sentry
  Sentry.addBreadcrumb({
    message: 'User initiated app update',
    category: 'user_action',
    level: 'info',
    data: {
      current_version: props.versionInfo.current_version,
      latest_version: props.versionInfo.latest_version,
      force_update: forceUpdate.value,
      persistent: props.persistent
    }
  });

  // Emitir evento de actualización - el composable maneja el reload
  emit('update');
};

const handleLater = () => {
  // Track user dismissal en Sentry
  Sentry.addBreadcrumb({
    message: 'User dismissed update notification',
    category: 'user_action',
    level: 'info',
    data: {
      current_version: props.versionInfo.current_version,
      latest_version: props.versionInfo.latest_version
    }
  });

  emit('dismiss');
  show.value = false;
};
</script>

<style scoped>
.v-card-title {
  padding-bottom: 16px;
}

.v-expansion-panel-text {
  max-height: 200px;
  overflow-y: auto;
}
</style>
